import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardBody,
  Table,
  Row,
  Col,
  Container,
  Button,
  Spinner,
} from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaWallet, FaExternalLinkAlt, FaSyncAlt, FaCopy } from "react-icons/fa";
import { SiEthereum, SiTether } from "react-icons/si";
import UserHeader from "components/Headers/UserHeader.js";

const theme = {
  bgPage: "#251d0c",
  bgCard: "#18181c",
  cardBorder: "rgba(255, 176, 0, 0.12)",
  accent: "#FFB000",
  text: "#f4f4f5",
  textMuted: "#71717a",
  success: "#22c55e",
  eth: "#627eea",
  usdt: "#26a17b",
  border: "rgba(255,255,255,0.08)",
};

const CONTENT_MAX_WIDTH = 1200;
const SPACING = 24;
const TRACKED_CONTRACT_ADDRESSES = ["0x18510c595592ee2a5c7623523664496da49f76c2"];

const TreasuryReserve = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const getToken = () => localStorage.getItem("token");

  const authFetch = useCallback(async (url, options = {}) => {
    const token = getToken();
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...options.headers,
    };
    const response = await fetch(url, { ...options, headers });

    if (response.status === 401 || response.status === 403) {
      localStorage.removeItem("token");
      window.location.href = "/auth/login";
      throw new Error("Authentication failed");
    }

    const text = await response.text();
    if (!response.ok) {
      let message = `HTTP error! status: ${response.status}`;
      try {
        const json = JSON.parse(text);
        message = json.message || message;
      } catch (_) {
        if (text) message = text;
      }
      throw new Error(message);
    }

    return text ? JSON.parse(text) : {};
  }, []);

  const fetchReserve = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      try {
        const result = await authFetch(
          `${process.env.REACT_APP_API_URL}/treasury-reserve`,
        );
        setData(result);
      } catch (error) {
        toast.error(error.message || "Failed to load treasury reserve");
        setData(null);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authFetch],
  );

  useEffect(() => {
    fetchReserve();
  }, [fetchReserve]);

  const shortAddress = (addr) => {
    if (!addr || addr.length < 12) return addr;
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const copyToClipboard = (text, label = "Address") => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Failed to copy"),
    );
  };

  const etherscanAddressUrl = (addr) => `https://etherscan.io/address/${addr}`;

  const getAssetIcon = (symbol) => {
    const s = (symbol || "").toUpperCase();
    if (s === "ETH")
      return <SiEthereum size={24} style={{ color: theme.eth }} />;
    if (s === "USDT" || s === "USDC")
      return <SiTether size={24} style={{ color: theme.usdt }} />;
    return (
      <span
        className="d-inline-flex align-items-center justify-content-center rounded-circle fw-bold"
        style={{
          width: 24,
          height: 24,
          background: "rgba(255,176,0,0.2)",
          color: theme.accent,
          fontSize: "0.7rem",
        }}
      >
        {(symbol || "?").slice(0, 1)}
      </span>
    );
  };

  if (loading) {
    return (
      <>
        <ToastContainer />
        <UserHeader />
        <Container
          fluid
          className="mt--7"
          style={{ background: theme.bgPage, minHeight: "60vh" }}
        >
          <div
            style={{
              maxWidth: CONTENT_MAX_WIDTH,
              margin: "0 auto",
              padding: SPACING,
            }}
          >
            <div className="d-flex flex-column align-items-center justify-content-center py-5">
              <Spinner
                style={{
                  color: theme.accent,
                  width: "2.5rem",
                  height: "2.5rem",
                }}
              />
              <span
                className="mt-3"
                style={{ color: theme.textMuted, fontSize: "0.9375rem" }}
              >
                Loading treasury reserve…
              </span>
            </div>
          </div>
        </Container>
      </>
    );
  }

  const assets = data?.assets || [];
  const wallet = data?.wallet || "";
  const normalizeWallet = (value) =>
    typeof value === "string" ? value.trim().toLowerCase() : "";
  const trackedWallets = [wallet, ...TRACKED_CONTRACT_ADDRESSES].filter(
    (address, index, addresses) => {
      const normalizedAddress = normalizeWallet(address);
      if (!normalizedAddress) return false;
      return (
        addresses.findIndex(
          (candidate) => normalizeWallet(candidate) === normalizedAddress,
        ) === index
      );
    },
  );
  const trackedWalletRows = trackedWallets.map((address) => {
    const isTreasury = normalizeWallet(address) === normalizeWallet(wallet);
    const matchingAsset = assets.find(
      (asset) =>
        normalizeWallet(asset?.contractAddress) === normalizeWallet(address),
    );

    return {
      address,
      type: isTreasury ? "Treasury" : "Contract"
    };
  });

  return (
    <>
      <ToastContainer />
      <UserHeader />
      <Container
        fluid
        className="mt--7"
        style={{ background: theme.bgPage, paddingBottom: 48 }}
      >
        <div
          style={{
            maxWidth: CONTENT_MAX_WIDTH,
            margin: "0 auto",
            padding: `0 ${SPACING}px`,
          }}
        >
          {/* Toolbar: title + refresh */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 20,
              marginBottom: SPACING,
            }}
          >
            <p
              className="mb-0"
              style={{ color: theme.textMuted, fontSize: "0.9375rem" }}
            >
              Balances and assets held in the reserve wallet
            </p>
            <Button
              color="warning"
              size="sm"
              onClick={() => fetchReserve(true)}
              disabled={refreshing}
              style={{
                background: theme.accent,
                border: "none",
                color: "#0f0f12",
                fontWeight: 600,
                borderRadius: 8,
                padding: "0.5rem 1rem",
                marginTop: 8,
              }}
            >
              {refreshing ? (
                <Spinner size="sm" className="me-3" />
              ) : (
                <FaSyncAlt className="me-3" />
              )}
              Refresh
            </Button>
          </div>

          {/* Overview card */}
          <Card
            className="border-0 mb-0"
            style={{
              background: theme.bgCard,
              border: `1px solid ${theme.cardBorder}`,
              borderRadius: 12,
              marginBottom: SPACING,
            }}
          >
            <CardBody style={{ padding: SPACING }}>
              <Row className="g-4">
                <Col xs="12">
                  <div className="d-flex align-items-start" style={{ gap: 16 }}>
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 48,
                        height: 48,
                        background: "rgba(255,176,0,0.12)",
                      }}
                    >
                      <FaWallet size={22} style={{ color: theme.accent }} />
                    </div>
                    <div className="min-w-0 flex-grow-1">
                      <div
                        className="text-uppercase mb-2"
                        style={{
                          color: theme.textMuted,
                          fontSize: "0.75rem",
                          letterSpacing: "0.05em",
                          fontWeight: 600,
                        }}
                      >
                        Reserve wallet
                      </div>
                      <div className="d-flex flex-column" style={{ gap: 10 }}>
                        {trackedWallets.length === 0 ? (
                          <span
                            className="font-monospace text-break"
                            style={{ color: theme.text, fontSize: "0.9375rem" }}
                          >
                            —
                          </span>
                        ) : (
                          trackedWallets.map((address) => (
                            <div
                              key={address}
                              className="d-flex align-items-center flex-wrap"
                              style={{ gap: 10 }}
                            >
                              <span
                                className="font-monospace text-break"
                                style={{ color: theme.text, fontSize: "0.9375rem" }}
                                title={address}
                              >
                                {address}
                              </span>
                              <button
                                type="button"
                                onClick={() => copyToClipboard(address, "Wallet")}
                                title="Copy"
                                className="border-0 bg-transparent p-0"
                                style={{
                                  color: theme.textMuted,
                                  cursor: "pointer",
                                  marginLeft: 4,
                                }}
                              >
                                <FaCopy size={14} />
                              </button>
                              <a
                                href={etherscanAddressUrl(address)}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: theme.accent, marginLeft: 8 }}
                                title="View on Etherscan"
                              >
                                <FaExternalLinkAlt size={14} />
                              </a>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card
            className="border-0"
            style={{
              background: theme.bgCard,
              border: `1px solid ${theme.cardBorder}`,
              borderRadius: 12,
              overflow: "hidden",
              marginBottom: SPACING,
            }}
          >
            <div
              style={{
                padding: "20px 24px",
                borderBottom: `1px solid ${theme.border}`,
              }}
            >
              <h6
                className="mb-0"
                style={{ color: theme.text, fontWeight: 600, fontSize: "1rem" }}
              >
                Tracked Wallets
              </h6>
              <p
                className="mb-0 mt-2"
                style={{ color: theme.textMuted, fontSize: "0.8125rem" }}
              >
                Treasury and contract addresses used for monitoring
              </p>
            </div>
            <div className="table-responsive">
              <Table className="align-middle mb-0" style={{ color: theme.text }}>
                <thead>
                  <tr style={{ background: "rgba(0,0,0,0.25)" }}>
                    <th
                      style={{
                        padding: "14px 24px",
                        border: 0,
                        color: theme.textMuted,
                        fontSize: "0.6875rem",
                        fontWeight: 600,
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                      }}
                    >
                      Type
                    </th>
                    <th
                      style={{
                        padding: "14px 24px",
                        border: 0,
                        color: theme.textMuted,
                        fontSize: "0.6875rem",
                        fontWeight: 600,
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                      }}
                    >
                      Address
                    </th>
                  
                  </tr>
                </thead>
                <tbody>
                  {trackedWalletRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={3}
                        style={{ padding: "16px 24px", color: theme.textMuted }}
                      >
                        No tracked wallets.
                      </td>
                    </tr>
                  ) : (
                    trackedWalletRows.map((entry) => (
                      <tr key={entry.address} className="treasury-reserve-row">
                        <td style={{ padding: "16px 24px" }}>
                          <span
                            className="small text-uppercase"
                            style={{
                              background:
                                entry.type === "Treasury"
                                  ? "rgba(255,176,0,0.15)"
                                  : "rgba(98,126,234,0.15)",
                              color:
                                entry.type === "Treasury" ? theme.accent : theme.eth,
                              padding: "0.25rem 0.5rem",
                              borderRadius: 6,
                              fontWeight: 600,
                            }}
                          >
                            {entry.type}
                          </span>
                        </td>
                        <td style={{ padding: "16px 24px" }}>
                          <div
                            className="d-flex align-items-center flex-wrap"
                            style={{ gap: 10 }}
                          >
                            <span
                              className="font-monospace text-break"
                              style={{ color: theme.text, fontSize: "0.875rem" }}
                              title={entry.address}
                            >
                              {entry.address}
                            </span>
                            <button
                              type="button"
                              onClick={() => copyToClipboard(entry.address, "Wallet")}
                              title="Copy"
                              className="border-0 bg-transparent p-0"
                              style={{ color: theme.textMuted, cursor: "pointer" }}
                            >
                              <FaCopy size={12} />
                            </button>
                            <a
                              href={etherscanAddressUrl(entry.address)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: theme.accent }}
                              title="View on Etherscan"
                            >
                              <FaExternalLinkAlt size={12} />
                            </a>
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "16px 24px",
                            textAlign: "right",
                            color: theme.success,
                            fontWeight: 600,
                          }}
                        >
                          {entry.value}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Card>

          {/* Assets table card */}
          <Card
            className="border-0"
            style={{
              background: theme.bgCard,
              border: `1px solid ${theme.cardBorder}`,
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "20px 24px",
                borderBottom: `1px solid ${theme.border}`,
              }}
            >
              <h6
                className="mb-0"
                style={{ color: theme.text, fontWeight: 600, fontSize: "1rem" }}
              >
                Assets
              </h6>
              <p
                className="mb-0 mt-2"
                style={{ color: theme.textMuted, fontSize: "0.8125rem" }}
              >
                {assets.length} asset{assets.length !== 1 ? "s" : ""} in reserve
              </p>
            </div>
            {assets.length === 0 ? (
              <div
                className="text-center py-5"
                style={{ color: theme.textMuted, fontSize: "0.9375rem" }}
              >
                No assets in reserve.
              </div>
            ) : (
              <div className="table-responsive">
                <Table
                  className="align-middle mb-0"
                  style={{ color: theme.text }}
                >
                  <thead>
                    <tr style={{ background: "rgba(0,0,0,0.25)" }}>
                      <th
                        style={{
                          padding: "14px 24px",
                          border: 0,
                          color: theme.textMuted,
                          fontSize: "0.6875rem",
                          fontWeight: 600,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                        }}
                      >
                        Asset
                      </th>
                      <th
                        style={{
                          padding: "14px 24px",
                          border: 0,
                          color: theme.textMuted,
                          fontSize: "0.6875rem",
                          fontWeight: 600,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                        }}
                      >
                        Type
                      </th>
                      <th
                        style={{
                          padding: "14px 24px",
                          border: 0,
                          color: theme.textMuted,
                          fontSize: "0.6875rem",
                          fontWeight: 600,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                        }}
                      >
                        Chain
                      </th>
                      <th
                        style={{
                          padding: "14px 24px",
                          border: 0,
                          color: theme.textMuted,
                          fontSize: "0.6875rem",
                          fontWeight: 600,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                          textAlign: "right",
                        }}
                      >
                        Balance
                      </th>
                      <th
                        style={{
                          padding: "14px 24px",
                          border: 0,
                          color: theme.textMuted,
                          fontSize: "0.6875rem",
                          fontWeight: 600,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                        }}
                      >
                        Contract
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {assets.map((asset, idx) => (
                      <tr
                        key={idx}
                        style={{
                          borderBottom: `1px solid ${theme.border}`,
                          transition: "background 0.15s ease",
                        }}
                        className="treasury-reserve-row"
                      >
                        <td style={{ padding: "16px 24px" }}>
                          <div
                            className="d-flex align-items-center"
                            style={{ gap: 12 }}
                          >
                            <div className="flex-shrink-0">
                              {getAssetIcon(asset.symbol)}
                            </div>
                            <div>
                              <div
                                className="fw-semibold"
                                style={{ color: theme.text }}
                              >
                                {asset.symbol || "—"}
                              </div>
                              <div
                                className="small mt-1"
                                style={{ color: theme.textMuted }}
                              >
                                {asset.name || "—"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "16px 24px" }}>
                          <span
                            className="small text-uppercase"
                            style={{
                              background: "rgba(255,255,255,0.06)",
                              color: theme.textMuted,
                              padding: "0.25rem 0.5rem",
                              borderRadius: 6,
                              fontWeight: 500,
                            }}
                          >
                            {asset.type || "—"}
                          </span>
                        </td>
                        <td style={{ padding: "16px 24px", color: theme.text }}>
                          {asset.chain || "—"}
                        </td>
                        <td
                          style={{ padding: "16px 24px", textAlign: "right" }}
                        >
                          <span
                            className="fw-semibold font-monospace"
                            style={{ color: theme.success }}
                          >
                            {asset.quantity != null
                              ? Number(asset.quantity).toLocaleString(
                                  undefined,
                                  { maximumFractionDigits: 6 },
                                )
                              : "—"}
                          </span>
                        </td>
                        <td style={{ padding: "16px 24px" }}>
                          {asset.contractAddress ? (
                            <div
                              className="d-flex align-items-center"
                              style={{ gap: 10 }}
                            >
                              <span
                                className="font-monospace small"
                                style={{ color: theme.textMuted }}
                              >
                                {shortAddress(asset.contractAddress)}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  copyToClipboard(
                                    asset.contractAddress,
                                    "Contract",
                                  )
                                }
                                title="Copy"
                                className="border-0 bg-transparent p-0"
                                style={{
                                  color: theme.textMuted,
                                  cursor: "pointer",
                                }}
                              >
                                <FaCopy size={12} />
                              </button>
                              <a
                                href={etherscanAddressUrl(
                                  asset.contractAddress,
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: theme.accent }}
                                title="Etherscan"
                              >
                                <FaExternalLinkAlt size={12} />
                              </a>
                            </div>
                          ) : (
                            <span style={{ color: theme.textMuted }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card>
        </div>
      </Container>
      <style>{`
        .treasury-reserve-row:hover { background: rgba(255,255,255,0.04) !important; }
      `}</style>
    </>
  );
};

export default TreasuryReserve;
